import React, { useState, useEffect, useMemo } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useLocation, Navigate, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import CountUp from 'react-countup';
import { FaFacebookF, FaInstagram, FaTwitter, FaYoutube, FaBars, FaTimes, FaPhone, FaEnvelope, FaMapMarkerAlt, FaClock, FaWhatsapp, FaChevronRight, FaStar, FaNewspaper, FaCalendarAlt, FaUsers, FaChalkboardTeacher, FaFileDownload, FaQuestionCircle, FaComments, FaBullhorn, FaTachometerAlt, FaUserPlus, FaClipboardList, FaImages, FaMoneyBillWave, FaGraduationCap, FaDownload, FaBell } from 'react-icons/fa';
import { apiService } from './services/api';
import AdminLayout from './components/AdminLayout';
import GalleryPage from './pages/Gallery';
import EventsPage from './pages/Events';
import StaffPage from './pages/Staff';
import AdmissionsPage from './pages/Admissions';
import HomePage from './pages/Home';
import EnhancedHomePage from './EnhancedHomePage';
import ContactPage from './pages/Contact';
import FeesPage from './pages/Fees';
import DownloadsPage from './pages/Downloads';
import TimetablePage from './pages/Timetable';
import AdmissionsManagement from './admin/AdmissionsManagement';
import ContactManagement from './admin/ContactManagement';
import IMAGES from './config/images';
import NewsManagement from './admin/NewsManagement';
import EventsManagement from './admin/EventsManagement';
import GalleryManagement from './admin/GalleryManagement';
import StaffManagement from './admin/StaffManagement';
import FeesManagement from './admin/FeesManagement';
import DownloadsManagement from './admin/DownloadsManagement';
import TimetableManagement from './admin/TimetableManagement';
import FAQManagement from './admin/FAQManagement';
import TestimonialsManagement from './admin/TestimonialsManagement';
import SocialLinksManagement from './admin/SocialLinksManagement';
import NewsletterSubscribers from './admin/NewsletterSubscribers';
import Settings from './admin/Settings';
import ChangePassword from './admin/ChangePassword';
import AcademicsPage from './pages/Academics';
import StudentLifePage from './pages/StudentLife';
import TestimonialsPage from './pages/Testimonials';
import FAQPage from './pages/FAQ';
import { SiteSettingsProvider, useSiteSettings } from './context/SiteSettingsContext';
import { resolvePublicMediaUrl } from './utils/mediaUrl';
import { buildVisibilityFromApi } from './config/publicNavigation';
import LeadershipSection from './components/LeadershipSection';

// Login Page Component
const LoginPage = () => {
  const [credentials, setCredentials] = useState({ username: '', password: '' });
  const navigate = useNavigate();
  const [error, setError] = useState('');

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      const response = await apiService.auth.login(credentials);
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('userRole', response.data.user?.role || '');
      navigate('/admin');
    } catch (err) {
      setError('Invalid credentials. Use: admin / admin123');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-900 to-blue-800 flex items-center justify-center p-4">
      <motion.div 
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-md"
      >
        <div className="text-center mb-8">
          <div className="w-20 h-20 bg-blue-900 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-white text-3xl font-bold">V</span>
          </div>
          <h1 className="text-2xl font-bold text-blue-900">Admin Login</h1>
          <p className="text-gray-600 mt-2">Vidya Vatika World School</p>
        </div>

        <form onSubmit={handleLogin} className="space-y-6">
          {error && (
            <div className="bg-red-50 text-red-600 p-3 rounded-lg text-sm">
              {error}
            </div>
          )}
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Username</label>
            <input
              type="text"
              value={credentials.username}
              onChange={(e) => setCredentials({ ...credentials, username: e.target.value })}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-900 focus:border-transparent"
              placeholder="Enter username"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Password</label>
            <input
              type="password"
              value={credentials.password}
              onChange={(e) => setCredentials({ ...credentials, password: e.target.value })}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-900 focus:border-transparent"
              placeholder="Enter password"
              required
            />
          </div>

          <button
            type="submit"
            className="w-full bg-blue-900 text-white py-3 rounded-lg hover:bg-blue-800 transition-all font-semibold shadow-lg"
          >
            Login
          </button>
        </form>

        <div className="mt-6 text-center text-sm text-gray-600">
          <p>Demo Credentials:</p>
          <p className="font-mono text-xs bg-gray-100 p-2 rounded mt-1">username: admin | password: admin123</p>
        </div>
      </motion.div>
    </div>
  );
};

// WhatsApp Chat Button Component
const WhatsAppChat = () => {
  const { whatsapp } = useSiteSettings();
  const whatsappNumber = whatsapp.WHATSAPP_NUMBER || '919034851777';
  const isEnabled = whatsapp.WHATSAPP_ENABLED !== false;

  // Don't show button if disabled
  if (!isEnabled) {
    return null;
  }

  const message = 'Hello, I would like to inquire about admissions.';

  return (
    <a
      href={`https://wa.me/${whatsappNumber}?text=${encodeURIComponent(message)}`}
      target="_blank"
      rel="noopener noreferrer"
      className="fixed bottom-6 right-6 z-50"
      style={{ marginBottom: '80px' }}
    >
      <motion.div
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        whileHover={{ scale: 1.1 }}
        className="bg-green-500 text-white w-16 h-16 rounded-full flex items-center justify-center shadow-2xl hover:bg-green-600 transition-all cursor-pointer"
      >
        <FaWhatsapp size={32} />
      </motion.div>
    </a>
  );
};

// Phone Call Button Component
const PhoneCallButton = () => {
  const { whatsapp } = useSiteSettings();
  const phoneNumber = whatsapp.WHATSAPP_NUMBER || '919034851777';

  return (
    <a
      href={`tel:+${phoneNumber}`}
      className="fixed bottom-6 right-6 z-50"
      style={{ marginBottom: '160px' }}
    >
      <motion.div
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        whileHover={{ scale: 1.1 }}
        className="bg-blue-600 text-white w-16 h-16 rounded-full flex items-center justify-center shadow-2xl hover:bg-blue-700 transition-all cursor-pointer"
      >
        <FaPhone size={28} />
      </motion.div>
    </a>
  );
};

// About Page Component
const AboutPage = () => {
  React.useEffect(() => {
    // Handle hash-based scrolling for infrastructure section
    if (window.location.hash === '#infrastructure') {
      setTimeout(() => {
        const element = document.getElementById('infrastructure');
        if (element) {
          const headerOffset = 80;
          const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
          const offsetPosition = elementPosition - headerOffset;

          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
          });
        }
      }, 100);
    }
  }, []);

  return (
    <div className="pt-20 min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-blue-900 to-blue-800 text-white py-20">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-5xl md:text-6xl font-bold font-display mb-4">About Vidya Vatika</h1>
          <p className="text-xl text-blue-100 max-w-3xl mx-auto">
            Premier CBSE Institution in Panipat – Nurturing Excellence Since 2008
          </p>
        </div>
      </section>

      {/* Leadership Message */}
      <section id="leadership">
        <LeadershipSection context="about" />
      </section>

      {/* Vision & Mission */}
      <section className="py-16 bg-gradient-to-br from-blue-50 to-blue-100">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-white rounded-2xl shadow-xl p-8 hover:shadow-2xl transition-all"
            >
              <div className="w-16 h-16 bg-blue-900 rounded-full flex items-center justify-center mb-4">
                <i className="fas fa-eye text-2xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold font-display text-blue-900 mb-4">Our Vision</h3>
              <p className="text-gray-700 leading-relaxed">
                To cultivate responsible, innovative global citizens grounded in Indian ethos and modern knowledge, who contribute positively to society and excel in their chosen fields.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-white rounded-2xl shadow-xl p-8 hover:shadow-2xl transition-all"
            >
              <div className="w-16 h-16 bg-accent rounded-full flex items-center justify-center mb-4">
                <i className="fas fa-bullseye text-2xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold font-display text-blue-900 mb-4">Our Mission</h3>
              <p className="text-gray-700 leading-relaxed">
                Deliver holistic, value-based education that fosters academic brilliance, character development, critical thinking, and life skills through innovative teaching methodologies.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section id="infrastructure">
        <StatsSection />
      </section>

      {/* Infrastructure Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold font-display text-blue-900 text-center mb-12">
            World-Class Infrastructure
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: '🏫',
                title: 'Modern Classrooms',
                desc: 'Spacious, well-ventilated classrooms with smart panels for interactive learning'
              },
              {
                icon: '🔬',
                title: 'Science Laboratories',
                desc: 'Fully equipped Physics, Chemistry, and Biology labs for practical learning'
              },
              {
                icon: '💻',
                title: 'Computer Labs',
                desc: 'State-of-the-art computer laboratories with latest configurations'
              },
              {
                icon: '📚',
                title: 'Digital Library',
                desc: 'Extensive collection of books, journals, and digital resources'
              },
              {
                icon: '⚽',
                title: 'Sports Complex',
                desc: 'Large playground and indoor sports facilities for holistic development'
              },
              {
                icon: '🎨',
                title: 'Arts & Activity Rooms',
                desc: 'Dedicated spaces for creative activities, music, and dance'
              }
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-gradient-to-br from-blue-50 to-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all"
              >
                <div className="text-5xl mb-4">{item.icon}</div>
                <h3 className="text-2xl font-bold text-blue-900 mb-3">{item.title}</h3>
                <p className="text-gray-700 leading-relaxed">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

// Stats Section Component
const StatsSection = () => {
  const stats = [
    { number: 1200, label: 'Happy Students', suffix: '+' },
    { number: 85, label: 'Qualified Faculty', suffix: '+' },
    { number: 18, label: 'Years Legacy', suffix: '+' },
    { number: 45, label: 'Awards Won', suffix: '+' }
  ];

  return (
    <section className="py-16 bg-white">
      <div className="container mx-auto px-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
          {stats.map((stat, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              className="text-center"
            >
              <div className="text-5xl md:text-6xl font-bold text-blue-900 mb-2">
                <CountUp end={stat.number} duration={2.5} suffix={stat.suffix} />
              </div>
              <p className="text-gray-600 font-medium">{stat.label}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// News Page Component
const NewsPage = () => {
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiService.news.getAll()
      .then(res => {
        setNews(res.data);
        setLoading(false);
      })
      .catch(() => {
        // Mock data fallback
        setNews([
          { id: 1, title: 'Admissions Open for 2026-27', description: 'Vidya Vatika World School announces open admissions for classes Pre-Nursery to XI.', category: 'Admission', publishedDate: new Date().toISOString() },
          { id: 2, title: 'Annual Sports Day', description: 'Grand sports day celebration on 15th April with various athletic events.', category: 'Events', publishedDate: new Date().toISOString() }
        ]);
        setLoading(false);
      });
  }, []);

  return (
    <div className="pt-20 min-h-screen bg-gray-50">
      <div className="bg-gradient-to-r from-blue-900 to-blue-800 text-white py-16">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-5xl font-bold font-display mb-4">News & Announcements</h1>
          <p className="text-xl text-blue-100">Stay updated with latest happenings</p>
        </div>
      </div>

      <div className="container mx-auto px-4 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {news.map((item, index) => (
            <motion.article
              key={item.id}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-all"
            >
              <div className="p-6">
                <span className="inline-block bg-accent text-white text-xs px-3 py-1 rounded-full mb-3">
                  {item.category}
                </span>
                <h3 className="text-xl font-bold text-blue-900 mb-3">{item.title}</h3>
                <p className="text-gray-600 mb-4">{item.description}</p>
                <p className="text-sm text-gray-500">
                  {new Date(item.publishedDate).toLocaleDateString('en-IN', { 
                    year: 'numeric', month: 'long', day: 'numeric' 
                  })}
                </p>
              </div>
            </motion.article>
          ))}
        </div>
      </div>
    </div>
  );
};

// Admin Dashboard Component
const AdminDashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    news: 0,
    events: 0,
    admissions: 0,
    staff: 0,
    contacts: 0,
    downloads: 0,
    fees: 0,
    timetables: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiService.getDashboardStats()
      .then(res => {
        console.log('📊 Dashboard stats:', res.data.stats);
        setStats(res.data.stats);
        setLoading(false);
      })
      .catch(err => {
        console.error('Error fetching dashboard stats:', err);
        setLoading(false);
      });
  }, []);

  const cardData = [
    { icon: FaNewspaper, label: 'Total News', count: stats.news, color: 'bg-blue-500', gradient: 'from-blue-500 to-blue-600' },
    { icon: FaCalendarAlt, label: 'Total Events', count: stats.events, color: 'bg-green-500', gradient: 'from-green-500 to-green-600' },
    { icon: FaUserPlus, label: 'Admission Enquiries', count: stats.admissions, color: 'bg-orange-500', gradient: 'from-orange-500 to-orange-600' },
    { icon: FaUsers, label: 'Total Staff', count: stats.staff, color: 'bg-purple-500', gradient: 'from-purple-500 to-purple-600' },
    { icon: FaEnvelope, label: 'Contact Messages', count: stats.contacts, color: 'bg-pink-500', gradient: 'from-pink-500 to-pink-600' },
    { icon: FaDownload, label: 'Downloads', count: stats.downloads, color: 'bg-indigo-500', gradient: 'from-indigo-500 to-indigo-600' },
    { icon: FaMoneyBillWave, label: 'Fee Structures', count: stats.fees, color: 'bg-emerald-500', gradient: 'from-emerald-500 to-emerald-600' },
    { icon: FaClock, label: 'Timetable Entries', count: stats.timetables, color: 'bg-cyan-500', gradient: 'from-cyan-500 to-cyan-600' }
  ];

  if (loading) {
    return (
      <AdminLayout>
        <div className="p-6">
          <div className="text-center text-xl text-gray-600">Loading dashboard...</div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-800 mb-2">Dashboard Overview</h1>
        <p className="text-gray-600">Real-time statistics from your school management system</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {cardData.map((stat, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.05 }}
            className={`bg-gradient-to-br ${stat.gradient} text-white rounded-xl shadow-lg p-6 transform hover:scale-105 transition-all duration-300 cursor-pointer`}
            onClick={() => {
              const routes = {
                'Total News': '/admin/news',
                'Total Events': '/admin/events',
                'Admission Enquiries': '/admin/admissions',
                'Total Staff': '/admin/staff',
                'Contact Messages': '/admin/contact',
                'Downloads': '/admin/downloads',
                'Fee Structures': '/admin/fees',
                'Timetable Entries': '/admin/timetable'
              };
              if (routes[stat.label]) navigate(routes[stat.label]);
            }}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm opacity-90">{stat.label}</p>
                <motion.p 
                  className="text-4xl font-bold mt-2"
                  initial={{ scale: 0.5, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ delay: index * 0.1 + 0.3, type: 'spring' }}
                >
                  {stat.count}
                </motion.p>
              </div>
              <div className="bg-white/20 p-4 rounded-full">
                <stat.icon className="text-4xl" />
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      <div className="bg-white rounded-xl shadow-lg p-6">
        <h2 className="text-xl font-bold text-gray-800 mb-4">Quick Actions</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Link to="/admin/news" className="p-4 bg-blue-50 rounded-lg hover:bg-blue-100 transition text-center">
            <FaNewspaper className="text-2xl text-blue-900 mx-auto mb-2" />
            <p className="text-sm font-medium">Manage News</p>
          </Link>
          <Link to="/admin/events" className="p-4 bg-green-50 rounded-lg hover:bg-green-100 transition text-center">
            <FaCalendarAlt className="text-2xl text-green-900 mx-auto mb-2" />
            <p className="text-sm font-medium">Manage Events</p>
          </Link>
          <Link to="/admin/admissions" className="p-4 bg-orange-50 rounded-lg hover:bg-orange-100 transition text-center">
            <FaUserPlus className="text-2xl text-orange-900 mx-auto mb-2" />
            <p className="text-sm font-medium">View Admissions</p>
          </Link>
          <Link to="/admin/staff" className="p-4 bg-purple-50 rounded-lg hover:bg-purple-100 transition text-center">
            <FaUsers className="text-2xl text-purple-900 mx-auto mb-2" />
            <p className="text-sm font-medium">Manage Staff</p>
          </Link>
          <Link to="/admin/contact" className="p-4 bg-pink-50 rounded-lg hover:bg-pink-100 transition text-center">
            <FaEnvelope className="text-2xl text-pink-900 mx-auto mb-2" />
            <p className="text-sm font-medium">Contact Messages</p>
          </Link>
          <Link to="/admin/downloads" className="p-4 bg-indigo-50 rounded-lg hover:bg-indigo-100 transition text-center">
            <FaDownload className="text-2xl text-indigo-900 mx-auto mb-2" />
            <p className="text-sm font-medium">Downloads</p>
          </Link>
          <Link to="/admin/fees" className="p-4 bg-emerald-50 rounded-lg hover:bg-emerald-100 transition text-center">
            <FaMoneyBillWave className="text-2xl text-emerald-900 mx-auto mb-2" />
            <p className="text-sm font-medium">Fee Structure</p>
          </Link>
          <Link to="/admin/settings" className="p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition text-center">
            <FaBell className="text-2xl text-gray-900 mx-auto mb-2" />
            <p className="text-sm font-medium">Settings</p>
          </Link>
        </div>
      </div>
    </AdminLayout>
  );
};

// Protected Route Wrapper
const ProtectedRoute = ({ children }) => {
  const token = localStorage.getItem('token');
  if (!token) {
    return <Navigate to="/login" replace />;
  }
  return children;
};

// Main App Component
function App() {
  const [menuItems, setMenuItems] = useState([]);
  const [menuVisibility, setMenuVisibility] = useState({});
  const [loading, setLoading] = useState(true);
  const [announcements, setAnnouncements] = useState(['Welcome to Vidya Vatika World School', 'Admissions Open 2026-27', 'Sports Day on March 15']);

  useEffect(() => {
    // Load menu visibility settings
    const loadMenuSettings = async () => {
      try {
        const response = await apiService.settings.getMenuVisibility();
        if (response.data && response.data.menu) {
          setMenuVisibility(buildVisibilityFromApi(response.data.menu));
        }
      } catch (error) {
        console.error('Error loading menu visibility:', error);
      }
    };

    loadMenuSettings();
  }, []);

  // Fetch announcements for news ticker
  useEffect(() => {
    const fetchAnnouncements = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/news`);
        if (response.ok) {
          const data = await response.json();
          setAnnouncements(data.slice(0, 5).map(item => item.title));
        }
      } catch (error) {
        console.error('Error fetching announcements:', error);
      }
    };
    fetchAnnouncements();
  }, []);

  useEffect(() => {
    apiService.getMenu()
      .then(res => setMenuItems(res.data))
      .catch(() => {
        // Minimal main menu with organized submenus
        setMenuItems([
          { key: 'home', label: 'Home', path: '/', icon: '🏠' },
          { 
            key: 'about', 
            label: 'About Us', 
            path: '/about',
            icon: 'ℹ️',
            submenu: [
              { key: 'leadership-message', label: 'Leadership Message', path: '/about#leadership' },
              { key: 'vision', label: 'Vision & Mission', path: '/about#vision' },
              { key: 'infrastructure', label: 'Infrastructure', path: '/about#infrastructure' },
              { key: 'staff', label: 'Our Staff', path: '/staff' }
            ]
          },
          { 
            key: 'academics', 
            label: 'Academics', 
            path: '/academics',
            icon: '📚',
            submenu: [
              { key: 'curriculum', label: 'Curriculum', path: '/academics#curriculum' },
              { key: 'assessment', label: 'Assessment', path: '/academics#assessment' },
              { key: 'admissions', label: 'Admissions', path: '/admissions' },
              { key: 'fees', label: 'Fee Structure', path: '/fees' },
              { key: 'timetable', label: 'Timetable', path: '/timetable' },
              { key: 'faq', label: 'FAQ', path: '/faq' }
            ]
          },
          { 
            key: 'student-life', 
            label: 'Student Life', 
            path: '/student-life',
            icon: '🎯',
            submenu: [
              { key: 'activities', label: 'Activities', path: '/student-life#activities' },
              { key: 'sports', label: 'Sports', path: '/student-life#sports' },
              { key: 'achievements', label: 'Achievements', path: '/student-life#achievements' },
              { key: 'testimonials', label: 'Testimonials', path: '/testimonials' }
            ]
          },
          { 
            key: 'news-events', 
            label: 'News & Events', 
            path: '/news',
            icon: '📰',
            submenu: [
              { key: 'news', label: 'Latest News', path: '/news' },
              { key: 'events', label: 'School Events', path: '/events' },
              { key: 'gallery', label: 'Photo Gallery', path: '/gallery' },
              { key: 'downloads', label: 'Downloads', path: '/downloads' }
            ]
          },
          { key: 'contact', label: 'Contact', path: '/contact', icon: '📞' }
        ]);
      })
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-blue-50 to-blue-100">
        <div className="text-center">
          <div className="spinner w-16 h-16 mx-auto mb-4"></div>
          <p className="text-blue-900 font-semibold text-lg animate-pulse">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <SiteSettingsProvider>
    <Router>
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50">
        <Routes>
          {/* Public Routes */}
          <Route path="/login" element={<LoginPage />} />
          
          {/* Admin Routes */}
          <Route path="/admin" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
          
          {/* Admin CRUD Pages */}
          <Route path="/admin/news" element={<ProtectedRoute><NewsManagement /></ProtectedRoute>} />
          <Route path="/admin/events" element={<ProtectedRoute><EventsManagement /></ProtectedRoute>} />
          <Route path="/admin/gallery" element={<ProtectedRoute><GalleryManagement /></ProtectedRoute>} />
          <Route path="/admin/staff" element={<ProtectedRoute><StaffManagement /></ProtectedRoute>} />
          <Route path="/admin/fees" element={<ProtectedRoute><FeesManagement /></ProtectedRoute>} />
          <Route path="/admin/downloads" element={<ProtectedRoute><DownloadsManagement /></ProtectedRoute>} />
          <Route path="/admin/timetable" element={<ProtectedRoute><TimetableManagement /></ProtectedRoute>} />
          <Route path="/admin/faq" element={<ProtectedRoute><FAQManagement /></ProtectedRoute>} />
          <Route path="/admin/testimonials" element={<ProtectedRoute><TestimonialsManagement /></ProtectedRoute>} />
          <Route path="/admin/social" element={<ProtectedRoute><SocialLinksManagement /></ProtectedRoute>} />
          <Route path="/admin/newsletter" element={<ProtectedRoute><NewsletterSubscribers /></ProtectedRoute>} />
          <Route path="/admin/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
          <Route path="/admin/admissions" element={<ProtectedRoute><AdmissionsManagement /></ProtectedRoute>} />
          <Route path="/admin/contact" element={<ProtectedRoute><ContactManagement /></ProtectedRoute>} />
          <Route path="/admin/change-password" element={<ProtectedRoute><ChangePassword /></ProtectedRoute>} />
          
          {/* Main App Routes */}
          <Route path="/" element={<>
            <Header menuItems={menuItems} menuVisibility={menuVisibility} announcements={announcements} />
            <EnhancedHomePage />
            <Footer />
            <WhatsAppChat />
            <PhoneCallButton />
          </>} />
          
          <Route path="/about" element={<>
            <Header menuItems={menuItems} menuVisibility={menuVisibility} announcements={announcements} />
            <AboutPage />
            <Footer />
            <WhatsAppChat />
            <PhoneCallButton />
          </>} />
          
          <Route path="/news" element={<>
            <Header menuItems={menuItems} menuVisibility={menuVisibility} announcements={announcements} />
            <NewsPage />
            <Footer />
            <WhatsAppChat />
            <PhoneCallButton />
          </>} />
          
          <Route path="/academics" element={<>
            <Header menuItems={menuItems} menuVisibility={menuVisibility} announcements={announcements} />
            <AcademicsPage />
            <Footer />
            <WhatsAppChat />
            <PhoneCallButton />
          </>} />
          
          <Route path="/fees" element={<>
            <Header menuItems={menuItems} menuVisibility={menuVisibility} announcements={announcements} />
            <FeesPage />
            <Footer />
            <WhatsAppChat />
            <PhoneCallButton />
          </>} />
          
          <Route path="/timetable" element={<>
            <Header menuItems={menuItems} menuVisibility={menuVisibility} announcements={announcements} />
            <TimetablePage />
            <Footer />
            <WhatsAppChat />
            <PhoneCallButton />
          </>} />
          
          <Route path="/downloads" element={<>
            <Header menuItems={menuItems} menuVisibility={menuVisibility} announcements={announcements} />
            <DownloadsPage />
            <Footer />
            <WhatsAppChat />
            <PhoneCallButton />
          </>} />
          
          <Route path="/student-life" element={<>
            <Header menuItems={menuItems} menuVisibility={menuVisibility} announcements={announcements} />
            <StudentLifePage />
            <Footer />
            <WhatsAppChat />
            <PhoneCallButton />
          </>} />
          
          <Route path="/testimonials" element={<>
            <Header menuItems={menuItems} menuVisibility={menuVisibility} announcements={announcements} />
            <TestimonialsPage />
            <Footer />
            <WhatsAppChat />
            <PhoneCallButton />
          </>} />
          
          <Route path="/faq" element={<>
            <Header menuItems={menuItems} menuVisibility={menuVisibility} announcements={announcements} />
            <FAQPage />
            <Footer />
            <WhatsAppChat />
            <PhoneCallButton />
          </>} />
          
          <Route path="/contact" element={<>
            <Header menuItems={menuItems} menuVisibility={menuVisibility} announcements={announcements} />
            <ContactPage />
            <Footer />
            <WhatsAppChat />
            <PhoneCallButton />
          </>} />
          
          <Route path="/gallery" element={<>
            <Header menuItems={menuItems} menuVisibility={menuVisibility} announcements={announcements} />
            <GalleryPage />
            <Footer />
            <WhatsAppChat />
            <PhoneCallButton />
          </>} />
          
          <Route path="/events" element={<>
            <Header menuItems={menuItems} menuVisibility={menuVisibility} announcements={announcements} />
            <EventsPage />
            <Footer />
            <WhatsAppChat />
            <PhoneCallButton />
          </>} />
          
          <Route path="/staff" element={<>
            <Header menuItems={menuItems} menuVisibility={menuVisibility} announcements={announcements} />
            <StaffPage />
            <Footer />
            <WhatsAppChat />
            <PhoneCallButton />
          </>} />
          
          <Route path="/admissions" element={<>
            <Header menuItems={menuItems} menuVisibility={menuVisibility} announcements={announcements} />
            <AdmissionsPage />
            <Footer />
            <WhatsAppChat />
            <PhoneCallButton />
          </>} />
          
          {/* Other routes can be added similarly */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </div>
    </Router>
    </SiteSettingsProvider>
  );
}

// Header Component (Enhanced with Logo)
const Header = ({ menuItems, menuVisibility, announcements }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [openSubmenu, setOpenSubmenu] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { school } = useSiteSettings();
  const logoSrc = resolvePublicMediaUrl(school.SCHOOL_LOGO_URL) || '/images/logo.png';

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Handle smooth scroll to sections
  const handleMenuClick = (path, e) => {
    if (path.includes('#')) {
      e.preventDefault();
      const [pagePath, anchorId] = path.split('#');
      
      // If we're on a different page, navigate first
      if (location.pathname !== pagePath) {
        navigate(pagePath);
        // Wait for navigation then scroll
        setTimeout(() => {
          const element = document.getElementById(anchorId);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }, 100);
      } else {
        // Already on the same page, just scroll
        const element = document.getElementById(anchorId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }
    }
    if (!path.includes('#')) {
      setIsMobileMenuOpen(false);
    }
  };

  const toggleSubmenu = (key) => {
    setOpenSubmenu(openSubmenu === key ? null : key);
  };

  // Filter + normalize menu items based on visibility settings (and remove duplicates)
  const filteredMenuItems = React.useMemo(() => {
    const list = Array.isArray(menuItems) ? menuItems : [];

    const isHidden = (key) => {
      if (!key) return false;
      if (menuVisibility) {
        const variants = [key, String(key).toLowerCase()];
        variants.push(String(key).replace(/-/g, '_'));
        variants.push(String(key).replace(/_/g, '-'));
        for (const v of variants) {
          if (Object.prototype.hasOwnProperty.call(menuVisibility, v)) {
            return menuVisibility[v] === false;
          }
        }
      }
      return false;
    };

    // Remove duplicates by path, keeping first occurrence.
    const dedupeByPath = (items) => {
      const seen = new Set();
      return items.filter((it) => {
        const p = String(it?.path || '');
        if (!p || p === '#') return true;
        if (seen.has(p)) return false;
        seen.add(p);
        return true;
      });
    };

    const normalizeItem = (item) => {
      if (!item) return null;

      const submenu = Array.isArray(item.submenu) ? item.submenu : [];
      const filteredSubmenu = dedupeByPath(
        submenu.filter((sub) => sub && !isHidden(sub.key))
      );

      const out = { ...item };
      if (submenu.length > 0) out.submenu = filteredSubmenu;
      return out;
    };

    const normalized = list
      .filter((item) => item?.key !== 'resources')
      .map(normalizeItem)
      .filter(Boolean)
      .filter((item) => {
        if (isHidden(item.key)) return false;
        // If parent has submenu and it's now empty, hide parent (except Home/Contact/About).
        if (Array.isArray(item.submenu) && item.submenu.length === 0 && !['home', 'about', 'contact'].includes(item.key)) {
          return false;
        }
        return true;
      });

    return dedupeByPath(normalized);
  }, [menuItems, menuVisibility]);

  return (
    <>
      {/* NEWS TICKER - Fixed at Very Top */}
      {announcements.length > 0 && (
        <div className="fixed top-0 left-0 right-0 z-[60] bg-gradient-to-r from-red-700 via-red-600 to-red-700 text-white py-1 overflow-hidden border-b border-white/10">
          <div className="container mx-auto px-4 flex items-center">
            <div className="bg-yellow-400 text-red-700 px-2 py-0.5 font-bold text-xs whitespace-nowrap animate-pulse flex items-center gap-1 rounded-full">
              <span className="text-xs">📢</span>
            </div>
            <div className="flex-1 overflow-hidden ml-2">
              <motion.div 
                className="flex gap-16"
                animate={{ x: [0, -800] }}
                transition={{ duration: 8, repeat: Infinity, ease: "linear" }}
              >
                {[...announcements, ...announcements].map((announcement, idx) => (
                  <span key={idx} className="text-xs whitespace-nowrap flex items-center gap-1">
                    <FaChevronRight className="text-yellow-400 text-[10px]" />
                    {announcement}
                  </span>
                ))}
              </motion.div>
            </div>
          </div>
        </div>
      )}
      
      <motion.header 
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled ? 'bg-blue-900/95 backdrop-blur-md shadow-lg py-2' : 'bg-blue-900 py-3'
        }`}
        style={{ marginTop: announcements.length > 0 ? '28px' : '0' }}
      >
      
      <nav className="container mx-auto px-4">
        <div className="flex justify-between items-center">
          <Link to="/" className="flex items-center space-x-3 group">
            <motion.img 
              whileHover={{ scale: 1.1 }}
              src={logoSrc}
              alt={`${school.SCHOOL_NAME} logo`}
              className="w-12 h-12 object-contain"
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
            <div>
              <h1 className="text-white font-bold text-xl md:text-2xl font-display">{school.SCHOOL_NAME}</h1>
              <p className="text-blue-200 text-xs hidden md:block">{school.SCHOOL_TAGLINE}</p>
            </div>
          </Link>

          <ul className="hidden lg:flex items-center space-x-6">
            {filteredMenuItems.map((item) => (
              <li key={item.key} className="relative group">
                <a
                  href={item.path}
                  onClick={(e) => handleMenuClick(item.path, e)}
                  className={`flex items-center gap-1 text-sm font-medium transition-all duration-300 relative py-2 ${
                    location.pathname === item.path || location.pathname.startsWith(item.path + '#') ? 'text-accent' : 'text-white hover:text-accent'
                  }`}
                >
                  {item.icon && (<span className="text-base">{item.icon}</span>)}
                  {item.label}
                  {item.submenu && (
                    <svg className="w-4 h-4 ml-1 inline-block" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  )}
                  <span className={`absolute bottom-0 left-0 h-0.5 bg-accent transition-all duration-300 ${
                    location.pathname === item.path ? 'w-full' : 'w-0 group-hover:w-full'
                  }`}></span>
                </a>
                
                {/* Desktop Dropdown Menu */}
                {item.submenu && (
                  <div className="absolute top-full left-0 mt-2 w-56 bg-white rounded-lg shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 transform translate-y-2 group-hover:translate-y-0 z-50">
                    <div className="py-2">
                      {item.submenu.map((subItem) => (
                        <a
                          key={subItem.key}
                          href={subItem.path}
                          onClick={(e) => handleMenuClick(subItem.path, e)}
                          className="block px-4 py-2 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-900 transition-colors"
                        >
                          {subItem.label}
                        </a>
                      ))}
                    </div>
                  </div>
                )}
              </li>
            ))}
            <li>
              <Link
                to="/login"
                className="bg-accent hover:bg-accent-dark text-white px-5 py-2 rounded-full font-semibold transition-all"
              >
                Admin Login
              </Link>
            </li>
          </ul>

          <button 
            className="lg:hidden text-white text-2xl"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? <FaTimes /> : <FaBars />}
          </button>
        </div>

        <AnimatePresence>
          {isMobileMenuOpen && (
            <motion.ul
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="lg:hidden mt-4 pb-4 space-y-2"
            >
              {filteredMenuItems.map((item) => (
                <li key={item.key}>
                  <div className="flex items-center justify-between">
                    <a
                      href={item.path}
                      onClick={(e) => {
                        handleMenuClick(item.path, e);
                        if (!item.submenu) setIsMobileMenuOpen(false);
                      }}
                      className="flex-1 py-2 px-3 rounded text-white hover:bg-blue-800 flex items-center gap-2"
                    >
                      {item.icon && (<span>{item.icon}</span>)}
                      {item.label}
                    </a>
                    {item.submenu && (
                      <button
                        onClick={() => toggleSubmenu(item.key)}
                        className="p-2 text-white hover:bg-blue-800 rounded"
                      >
                        <svg 
                          className={`w-5 h-5 transition-transform duration-300 ${openSubmenu === item.key ? 'rotate-180' : ''}`}
                          fill="none" 
                          stroke="currentColor" 
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>
                    )}
                  </div>
                  {/* Mobile Submenu (Accordion) */}
                  {item.submenu && openSubmenu === item.key && (
                    <motion.ul
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      className="ml-4 mt-2 space-y-1 bg-blue-800/50 rounded-lg overflow-hidden"
                    >
                      {item.submenu.map((subItem) => (
                        <li key={subItem.key}>
                          <a
                            href={subItem.path}
                            onClick={(e) => {
                              handleMenuClick(subItem.path, e);
                              setIsMobileMenuOpen(false);
                            }}
                            className="block py-2 px-3 text-sm text-blue-100 hover:bg-blue-700 rounded"
                          >
                            {subItem.label}
                          </a>
                        </li>
                      ))}
                    </motion.ul>
                  )}
                </li>
              ))}
              <li>
                <Link
                  to="/login"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="block py-2 px-3 bg-accent text-white rounded text-center font-semibold"
                >
                  Admin Login
                </Link>
              </li>
            </motion.ul>
          )}
        </AnimatePresence>
      </nav>
    </motion.header>
    </>
  );
};

// Footer Component (Enhanced with Social Icons)
const Footer = () => {
  const { school, social } = useSiteSettings();
  const logoSrc = resolvePublicMediaUrl(school.SCHOOL_LOGO_URL) || '/images/logo.png';

  const socialLinks = useMemo(() => {
    const base = [
      { platform: 'Facebook', key: 'SOCIAL_FACEBOOK', url: 'https://www.facebook.com/vidyavatikaworld', icon: FaFacebookF },
      { platform: 'Instagram', key: 'SOCIAL_INSTAGRAM', url: 'https://www.instagram.com/vidyavatikaworld', icon: FaInstagram },
      { platform: 'Twitter', key: 'SOCIAL_TWITTER', url: 'https://x.com/VidyaVatikaPNP', icon: FaTwitter },
      { platform: 'YouTube', key: 'SOCIAL_YOUTUBE', url: '', icon: FaYoutube },
    ];
    return base
      .map((l) => ({ ...l, url: social[l.key] || l.url }))
      .filter((l) => l.platform !== 'YouTube' || (social.SOCIAL_YOUTUBE && String(social.SOCIAL_YOUTUBE).trim() !== ''));
  }, [social]);

  const phoneDigits = String(school.SCHOOL_PHONE || '').replace(/\D/g, '');
  const telHref = phoneDigits.length >= 10 ? `tel:+91${phoneDigits.slice(-10)}` : `tel:${school.SCHOOL_PHONE}`;

  return (
    <footer className="bg-blue-900 text-white pt-12 pb-6">
      <div className="container mx-auto px-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
          <div>
            <div className="flex items-center space-x-3 mb-4">
              <img src={logoSrc} alt="" className="w-12 h-12 object-contain" />
              <h3 className="font-display font-bold text-xl">{school.SCHOOL_NAME}</h3>
            </div>
            <p className="text-blue-200 text-sm">{school.SCHOOL_TAGLINE}</p>
          </div>

          <div>
            <h4 className="font-semibold text-lg mb-4">Quick Links</h4>
            <ul className="space-y-2 text-sm text-blue-200">
              <li><Link to="/admissions" className="hover:text-accent">Admissions</Link></li>
              <li><Link to="/events" className="hover:text-accent">Events</Link></li>
              <li><Link to="/gallery" className="hover:text-accent">Gallery</Link></li>
              <li><Link to="/contact" className="hover:text-accent">Contact</Link></li>
            </ul>
          </div>

          <div>
            <h4 className="font-semibold text-lg mb-4">Contact</h4>
            <ul className="space-y-3 text-sm text-blue-200">
              <li className="flex items-start space-x-2">
                <FaMapMarkerAlt className="mt-1 text-accent" />
                <span>{school.SCHOOL_ADDRESS}</span>
              </li>
              <li className="flex items-center space-x-2">
                <FaPhone className="text-accent" />
                <a href={telHref} className="hover:text-white">{school.SCHOOL_PHONE}</a>
              </li>
              <li className="flex items-center space-x-2">
                <FaEnvelope className="text-accent" />
                <a href={`mailto:${school.SCHOOL_EMAIL_INFO}`} className="hover:text-white break-all">{school.SCHOOL_EMAIL_INFO}</a>
              </li>
            </ul>
          </div>

          <div>
            <h4 className="font-semibold text-lg mb-4">Follow Us</h4>
            <div className="flex space-x-3">
              {socialLinks.filter((link) => link.url && String(link.url).trim()).map((link) => (
                <a
                  key={link.platform}
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-blue-800 rounded-full flex items-center justify-center hover:bg-accent transition-all duration-300 transform hover:scale-110"
                >
                  <link.icon className="text-white" />
                </a>
              ))}
            </div>
          </div>
        </div>

        <div className="border-t border-blue-800 pt-6 text-center text-sm text-blue-300">
          <p>&copy; {new Date().getFullYear()} {school.SCHOOL_NAME}. All Rights Reserved.</p>
        </div>
      </div>
    </footer>
  );
};

// Enhanced HomePage
export default App;
